import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { authFetch } from "@/lib/authFetch";
import { handlePlainTextCopy } from "@/lib/plainTextCopy";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  MessageCircle, Plus, Loader2, PhoneCall, Wrench, Bot, Send,
  Paperclip, X, ChevronLeft, Lock, ImageIcon, Video, FileText,
  Copy, MoreHorizontal, UserCheck, Users, Building2, RefreshCw,
  Clock, AlertCircle, CheckCircle2, ChevronDown, Search, Filter,
  Maximize2, Download, ArrowLeft, Info, Camera,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// ─── Types ────────────────────────────────────────────────────────────────────

type Tab = "nouveau" | "mes_chats" | "archive" | "tous_ouverts";
type Statut = "nouveau" | "ouvert" | "attente_revendeur" | "escalade_pudu" | "ferme";
type TypeDemande = "information" | "installation" | "sav";

interface ChatSummary {
  id: string; numero: string; typeDemande: TypeDemande; titre: string;
  statut: Statut; robotSn?: string; clientFinalNom?: string;
  createdAt: string; updatedAt: string; lastMessageAt?: string; assigneAt?: string;
  technicienJobottoId?: string;
  reseller?: { id: string; nom: string; codeCourt?: string; logoUrl?: string };
  technicien?: { id: string; prenom: string; nom: string; avatarUrl?: string };
  lastMessage?: { contenu: string; auteurType: string; createdAt: string };
  isUnread: boolean;
  attachments: { images: number; videos: number; docs: number };
}

interface ChatDetail {
  id: string; numero: string; typeDemande: TypeDemande; titre: string;
  statut: Statut; robotSn?: string; robotId?: string; clientFinalNom?: string;
  clientFinalAdresse?: string; problemeConstate?: string;
  technicienJobottoId?: string; assigneAt?: string; createdAt: string;
  fermeAt?: string;
  resellers?: { id: string; company_name: string; code_court?: string; logo_url?: string; phone?: string; email?: string };
  profiles?: { id: string; first_name: string; last_name: string; avatar_url?: string };
  ouvert_par_portail_user?: { id: string; prenom?: string; nom?: string; email?: string };
  pudu_robots?: { id: string; robot_sn: string; robot_type?: string; robot_name?: string; status?: string; last_seen?: string };
}

interface ChatParticipant {
  id: string; type_acteur: string; ajoute_at: string;
  profiles?: { id: string; first_name: string; last_name: string; avatar_url?: string; email?: string };
  portail_users?: { id: string; prenom?: string; nom?: string; email?: string };
}

interface ChatMessage {
  id: string; chat_id: string; auteur_type: string;
  auteur_user_id?: string; auteur_portail_user_id?: string;
  contenu?: string; est_note_interne: boolean; created_at: string; lu_par: string[];
  profiles?: { id: string; first_name: string; last_name: string; avatar_url?: string };
  portail_users?: { id: string; prenom?: string; nom?: string };
  chat_attachments?: ChatAttachment[];
}

interface ChatAttachment {
  id: string; filename: string; mime_type: string; size_bytes: number;
  storage_path: string; type_media?: string; signedUrl?: string;
}

interface Profile {
  id: string; firstName?: string; lastName?: string; email?: string; avatarUrl?: string; isActive?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<TypeDemande, string> = {
  information: "Info", installation: "Install.", sav: "SAV",
};

const TYPE_COLORS: Record<TypeDemande, string> = {
  information: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  installation: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  sav: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const TYPE_ICON: Record<TypeDemande, typeof PhoneCall> = {
  information: PhoneCall, installation: Wrench, sav: Bot,
};

const STATUT_LABELS: Record<Statut, string> = {
  nouveau: "Nouveau", ouvert: "Ouvert", attente_revendeur: "En attente",
  escalade_pudu: "Escaladé Pudu", ferme: "Fermé",
};

const STATUT_COLORS: Record<Statut, string> = {
  nouveau: "bg-blue-100 text-blue-700",
  ouvert: "bg-green-100 text-green-700",
  attente_revendeur: "bg-amber-100 text-amber-700",
  escalade_pudu: "bg-orange-100 text-orange-700",
  ferme: "bg-muted text-muted-foreground",
};

function relativeDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = Date.now();
  const diff = now - d.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 2) return "à l'instant";
  if (mins < 60) return `il y a ${mins} min`;
  if (hours < 24) return `il y a ${hours}h`;
  if (days === 1) return "hier";
  if (days < 7) return `il y a ${days}j`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function initiales(prenom?: string | null, nom?: string | null): string {
  return ((prenom?.[0] ?? "") + (nom?.[0] ?? "")).toUpperCase() || "?";
}

const ALLOWED_MIME = [
  "image/jpeg", "image/png", "image/gif", "image/webp", "image/heic",
  "video/mp4", "video/quicktime", "video/webm", "video/avi",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ChatsInbox() {
  const { id: paramId } = useParams<{ id?: string }>();
  const [, navigate] = useLocation();
  const { profile } = useAuth();
  const { toast } = useToast();

  const [tab, setTab] = useState<Tab>("nouveau");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(paramId || null);
  const [mobileShowDetail, setMobileShowDetail] = useState(!!paramId);

  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [counts, setCounts] = useState({ nouveau: 0, mesChats: 0, archive: 0, tousOuverts: 0 });

  const [detail, setDetail] = useState<ChatDetail | null>(null);
  const [participants, setParticipants] = useState<ChatParticipant[]>([]);
  const [recentSn, setRecentSn] = useState<any[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [lastMessageAt, setLastMessageAt] = useState<string | null>(null);

  const [allProfiles, setAllProfiles] = useState<Profile[]>([]);
  const [infoSidebarOpen, setInfoSidebarOpen] = useState(true);

  // Modals
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeNote, setCloseNote] = useState("");
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState<string>("");
  const [actionLoading, setActionLoading] = useState(false);

  // Lightbox
  const [lightboxImages, setLightboxImages] = useState<ChatAttachment[]>([]);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  // Compose
  const [composeMode, setComposeMode] = useState<"reply" | "note">("reply");
  const [composeText, setComposeText] = useState("");
  const [composeFiles, setComposeFiles] = useState<File[]>([]);
  const [composeSending, setComposeSending] = useState(false);
  const [composeDragging, setComposeDragging] = useState(false);
  const composeFileRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const listPollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const composeCameraRef = useRef<HTMLInputElement>(null);
  const [mobileInfoOpen, setMobileInfoOpen] = useState(false);

  // ── Debounce search ────────────────────────────────────────────────────────
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 300);
  }, [search]);

  // ── Sync URL param → selectedId ───────────────────────────────────────────
  useEffect(() => {
    if (paramId && paramId !== selectedId) {
      setSelectedId(paramId);
      setMobileShowDetail(true);
    }
  }, [paramId]);

  // ── Load profiles (for reassign) ──────────────────────────────────────────
  useEffect(() => {
    authFetch("/api/profiles").then(r => r.json()).then(d => {
      setAllProfiles((d || []).map((p: any) => ({
        id: p.id, firstName: p.firstName, lastName: p.lastName,
        email: p.email, avatarUrl: p.avatarUrl, isActive: p.isActive,
      })));
    }).catch(() => {});
  }, []);

  // ── Fetch chat list ────────────────────────────────────────────────────────
  const fetchChats = useCallback(async () => {
    setChatsLoading(true);
    try {
      const params = new URLSearchParams({ tab, search: debouncedSearch });
      const [listRes, countRes] = await Promise.all([
        authFetch(`/api/chats?${params}`),
        authFetch("/api/chats/counts"),
      ]);
      const listData = await listRes.json();
      const countData = await countRes.json();
      setChats(listData.chats || []);
      setCounts(countData);
    } catch {}
    setChatsLoading(false);
  }, [tab, debouncedSearch]);

  useEffect(() => { fetchChats(); }, [fetchChats]);

  // ── Poll list every 15s (keeps unread indicators live) ────────────────────
  useEffect(() => {
    if (listPollTimer.current) clearInterval(listPollTimer.current);
    listPollTimer.current = setInterval(() => fetchChats(), 15_000);
    return () => { if (listPollTimer.current) clearInterval(listPollTimer.current); };
  }, [fetchChats]);

  // ── Refetch list on window focus ───────────────────────────────────────────
  useEffect(() => {
    const handler = () => fetchChats();
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, [fetchChats]);

  // ── Tab title with unread count ────────────────────────────────────────────
  useEffect(() => {
    const unreadCount = chats.filter(c => c.isUnread && c.id !== selectedId).length;
    document.title = unreadCount > 0
      ? `(${unreadCount}) Chats SAV · Jobotech`
      : "Chats SAV · Jobotech";
    return () => { document.title = "Jobotech"; };
  }, [chats, selectedId]);

  // ── Fetch detail + messages when selectedId changes ────────────────────────
  useEffect(() => {
    if (!selectedId) { setDetail(null); setMessages([]); setLastMessageAt(null); return; }
    setMessagesLoading(true);

    Promise.all([
      authFetch(`/api/chats/${selectedId}`),
      authFetch(`/api/chats/${selectedId}/messages`),
    ]).then(async ([dRes, mRes]) => {
      const dData = await dRes.json();
      const mData = await mRes.json();
      setDetail(dData.chat || null);
      setParticipants(dData.participants || []);
      setRecentSn(dData.recentSameSnChats || []);
      const msgs: ChatMessage[] = mData.messages || [];
      setMessages(msgs);
      if (msgs.length > 0) setLastMessageAt(msgs[msgs.length - 1].created_at);
      setMessagesLoading(false);

      // Mark as read
      authFetch(`/api/chats/${selectedId}/mark-read`, { method: "POST" }).catch(() => {});
    }).catch(() => setMessagesLoading(false));
  }, [selectedId]);

  // ── Scroll to bottom on new messages ──────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // ── Poll for new messages every 10s ───────────────────────────────────────
  useEffect(() => {
    if (!selectedId) return;
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = setInterval(async () => {
      try {
        const after = lastMessageAt;
        const params = after ? `?after=${encodeURIComponent(after)}` : "";
        const r = await authFetch(`/api/chats/${selectedId}/messages${params}`);
        const d = await r.json();
        const newMsgs: ChatMessage[] = d.messages || [];
        if (newMsgs.length > 0) {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const added = newMsgs.filter(m => !existingIds.has(m.id));
            if (added.length === 0) return prev;
            return [...prev, ...added];
          });
          setLastMessageAt(newMsgs[newMsgs.length - 1].created_at);
          authFetch(`/api/chats/${selectedId}/mark-read`, { method: "POST" }).catch(() => {});
        }
      } catch {}
    }, 10_000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [selectedId, lastMessageAt]);

  // ── Select chat ────────────────────────────────────────────────────────────
  const selectChat = (id: string) => {
    setSelectedId(id);
    setMobileShowDetail(true);
    navigate(`/chats/${id}`, { replace: true });
  };

  const backToList = () => {
    setMobileShowDetail(false);
    navigate("/chats", { replace: true });
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleAssign = async (chatId: string) => {
    setActionLoading(true);
    try {
      const r = await authFetch(`/api/chats/${chatId}/assign`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) { toast({ title: d.message, variant: "destructive" }); return; }
      toast({ title: "Chat pris en charge ✓" });
      fetchChats();
      if (selectedId === chatId) {
        setDetail(d.chat);
        // Reload messages
        const mr = await authFetch(`/api/chats/${chatId}/messages`);
        const md = await mr.json();
        setMessages(md.messages || []);
      }
    } catch { toast({ title: "Erreur", variant: "destructive" }); }
    setActionLoading(false);
  };

  const handleStatusChange = async (chatId: string, statut: string) => {
    setActionLoading(true);
    try {
      const r = await authFetch(`/api/chats/${chatId}/status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ statut }),
      });
      const d = await r.json();
      if (!r.ok) { toast({ title: d.message, variant: "destructive" }); return; }
      setDetail(d.chat);
      fetchChats();
      const mr = await authFetch(`/api/chats/${chatId}/messages`);
      const md = await mr.json();
      setMessages(md.messages || []);
    } catch { toast({ title: "Erreur", variant: "destructive" }); }
    setActionLoading(false);
  };

  const handleClose = async () => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      const r = await authFetch(`/api/chats/${selectedId}/close`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ noteClôture: closeNote }),
      });
      const d = await r.json();
      if (!r.ok) { toast({ title: d.message, variant: "destructive" }); return; }
      setDetail(d.chat);
      setCloseModalOpen(false);
      setCloseNote("");
      fetchChats();
      const mr = await authFetch(`/api/chats/${selectedId}/messages`);
      setMessages((await mr.json()).messages || []);
    } catch { toast({ title: "Erreur", variant: "destructive" }); }
    setActionLoading(false);
  };

  const handleReopen = async () => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      const r = await authFetch(`/api/chats/${selectedId}/reopen`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) { toast({ title: d.message, variant: "destructive" }); return; }
      setDetail(d.chat);
      fetchChats();
      const mr = await authFetch(`/api/chats/${selectedId}/messages`);
      setMessages((await mr.json()).messages || []);
    } catch { toast({ title: "Erreur", variant: "destructive" }); }
    setActionLoading(false);
  };

  const handleReassign = async () => {
    if (!selectedId || !reassignTarget) return;
    setActionLoading(true);
    try {
      const r = await authFetch(`/api/chats/${selectedId}/reassign`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newTechnicienId: reassignTarget }),
      });
      const d = await r.json();
      if (!r.ok) { toast({ title: d.message, variant: "destructive" }); return; }
      toast({ title: "Chat réassigné ✓" });
      setDetail(d.chat);
      setReassignModalOpen(false);
      fetchChats();
      const mr = await authFetch(`/api/chats/${selectedId}/messages`);
      setMessages((await mr.json()).messages || []);
    } catch { toast({ title: "Erreur", variant: "destructive" }); }
    setActionLoading(false);
  };

  const handleSendMessage = async () => {
    if (!selectedId) return;
    if (!composeText.trim() && composeFiles.length === 0) return;
    setComposeSending(true);
    try {
      const fd = new FormData();
      fd.append("contenu", composeText);
      fd.append("estNoteInterne", String(composeMode === "note"));
      composeFiles.forEach(f => fd.append("attachments", f));

      const r = await authFetch(`/api/chats/${selectedId}/messages`, { method: "POST", body: fd });
      const d = await r.json();
      if (!r.ok) { toast({ title: d.message, variant: "destructive" }); return; }

      setComposeText("");
      setComposeFiles([]);
      setComposeMode("reply");

      // Reload messages
      const mr = await authFetch(`/api/chats/${selectedId}/messages`);
      const md = await mr.json();
      setMessages(md.messages || []);
      if (md.messages?.length > 0) setLastMessageAt(md.messages[md.messages.length - 1].created_at);
      fetchChats();
    } catch { toast({ title: "Erreur lors de l'envoi", variant: "destructive" }); }
    setComposeSending(false);
  };

  const handleComposeFiles = (files: File[]) => {
    const valid = files.filter(f => ALLOWED_MIME.includes(f.type));
    setComposeFiles(prev => [...prev, ...valid].slice(0, 10));
  };

  const handleComposeDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setComposeDragging(false);
    handleComposeFiles(Array.from(e.dataTransfer.files));
  }, []);

  const handleComposeKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); handleSendMessage(); }
  };

  // ─ Tabs config
  const TABS: { key: Tab; label: string; count: number }[] = [
    { key: "nouveau", label: "🔵 Nouveaux", count: counts.nouveau },
    { key: "mes_chats", label: "🟠 Mes chats", count: counts.mesChats },
    { key: "tous_ouverts", label: "Tous les ouverts", count: counts.tousOuverts },
    { key: "archive", label: "⚪ Archive", count: counts.archive },
  ];

  const isDetailOpen = !!selectedId && (mobileShowDetail || true);

  return (
    <div className="h-[calc(100dvh-8rem)] flex flex-col -m-4 md:-m-8">
      {/* Lightbox */}
      {lightboxImages.length > 0 && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={() => setLightboxImages([])}>
          <button className="absolute top-4 right-4 text-white/70 hover:text-white" onClick={() => setLightboxImages([])}><X className="w-6 h-6" /></button>
          {lightboxImages.length > 1 && (
            <>
              <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2" onClick={e => { e.stopPropagation(); setLightboxIdx(i => (i - 1 + lightboxImages.length) % lightboxImages.length); }}>
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2" onClick={e => { e.stopPropagation(); setLightboxIdx(i => (i + 1) % lightboxImages.length); }}>
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          <img
            src={lightboxImages[lightboxIdx]?.signedUrl || ""}
            alt={lightboxImages[lightboxIdx]?.filename}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
          <p className="absolute bottom-4 text-white/60 text-sm">{lightboxImages[lightboxIdx]?.filename} · {lightboxIdx + 1} / {lightboxImages.length}</p>
        </div>
      )}

      {/* Modal fermeture */}
      <Dialog open={closeModalOpen} onOpenChange={setCloseModalOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Fermer le chat</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Le revendeur sera notifié par email. Vous pouvez ajouter une note de clôture.</p>
          <textarea
            value={closeNote}
            onChange={e => setCloseNote(e.target.value)}
            placeholder="Note de clôture (optionnel)…"
            rows={3}
            className="w-full px-3 py-2 bg-input border border-input rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseModalOpen(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleClose} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Fermer le chat"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal réassignation */}
      <Dialog open={reassignModalOpen} onOpenChange={setReassignModalOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Réassigner le chat</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {allProfiles.filter(p => p.isActive !== false && p.id !== profile?.id).map(p => (
              <button
                key={p.id}
                onClick={() => setReassignTarget(p.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                  reassignTarget === p.id ? "bg-primary/10 border border-primary" : "border border-border hover:bg-secondary"
                )}
              >
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={p.avatarUrl || undefined} />
                  <AvatarFallback className="text-xs">{initiales(p.firstName, p.lastName)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">{p.firstName} {p.lastName}</p>
                  <p className="text-xs text-muted-foreground">{p.email}</p>
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignModalOpen(false)}>Annuler</Button>
            <Button onClick={handleReassign} disabled={!reassignTarget || actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Réassigner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex-1 flex overflow-hidden">
        {/* ── LEFT PANEL: Chat list ── */}
        <div className={cn(
          "flex flex-col border-r border-border bg-card shrink-0 overflow-hidden",
          "w-full md:w-[340px] lg:w-[360px]",
          mobileShowDetail ? "hidden md:flex" : "flex"
        )}>
          {/* Header */}
          <div className="px-4 pt-4 pb-2 border-b border-border/50 shrink-0">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-lg font-bold flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-primary" />
                Chats SAV
              </h1>
              <button
                data-testid="button-refresh-chats"
                onClick={fetchChats}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-0.5 overflow-x-auto scrollbar-hide">
              {TABS.map(t => (
                <button
                  key={t.key}
                  data-testid={`tab-${t.key}`}
                  onClick={() => { setTab(t.key); setSelectedId(null); navigate("/chats", { replace: true }); }}
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors shrink-0",
                    tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  )}
                >
                  {t.label}
                  {t.count > 0 && (
                    <span className={cn("min-w-[16px] h-4 text-[10px] font-bold rounded-full flex items-center justify-center px-1",
                      tab === t.key ? "bg-white/20 text-white" : "bg-red-500 text-white"
                    )}>
                      {t.count > 99 ? "99+" : t.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative mt-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                data-testid="input-search-chats"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher par numéro, titre, SN…"
                className="w-full pl-8 pr-3 py-1.5 bg-secondary/50 border border-border/50 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {chatsLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : chats.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-3 px-6">
                <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">Aucun chat dans cet onglet</p>
              </div>
            ) : (
              chats.map(chat => (
                <ChatCard
                  key={chat.id}
                  chat={chat}
                  isSelected={chat.id === selectedId}
                  currentUserId={profile?.id || ""}
                  tab={tab}
                  onSelect={selectChat}
                  onAssign={handleAssign}
                  assignLoading={actionLoading}
                />
              ))
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL: Detail ── */}
        <div className={cn(
          "flex-1 flex flex-col overflow-hidden bg-background",
          mobileShowDetail ? "flex" : "hidden md:flex"
        )}>
          {!selectedId || !detail ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 p-8">
              <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium text-foreground">Sélectionnez un chat</p>
                <p className="text-sm text-muted-foreground mt-1">Cliquez sur un chat dans la liste pour voir la conversation.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Detail Header */}
              <div className="border-b border-border bg-card px-4 py-3 shrink-0">
                <div className="flex items-start gap-3">
                  {/* Back button (mobile) */}
                  <button
                    className="md:hidden p-1 text-muted-foreground hover:text-foreground mt-0.5"
                    onClick={backToList}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-muted-foreground">{detail.numero}</span>
                      <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", TYPE_COLORS[detail.typeDemande])}>
                        {TYPE_LABELS[detail.typeDemande]}
                      </span>
                      {detail.resellers && (
                        <span className="text-xs text-muted-foreground">· {detail.resellers.company_name}</span>
                      )}
                    </div>
                    <p className="font-semibold text-foreground mt-0.5 truncate">{detail.titre}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* Statut dropdown */}
                    {detail.statut !== "ferme" && detail.statut !== "nouveau" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className={cn("flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors", STATUT_COLORS[detail.statut])}>
                            {STATUT_LABELS[detail.statut]}
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {(["ouvert", "attente_revendeur", "escalade_pudu"] as const).filter(s => s !== detail.statut).map(s => (
                            <DropdownMenuItem key={s} onClick={() => handleStatusChange(detail.id, s)}>
                              <span className={cn("w-2 h-2 rounded-full mr-2 shrink-0", {
                                "bg-green-500": s === "ouvert",
                                "bg-amber-500": s === "attente_revendeur",
                                "bg-orange-500": s === "escalade_pudu",
                              })} />
                              {STATUT_LABELS[s]}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}

                    {detail.statut === "nouveau" && (
                      <span className={cn("flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium", STATUT_COLORS.nouveau)}>
                        {STATUT_LABELS.nouveau}
                      </span>
                    )}
                    {detail.statut === "ferme" && (
                      <span className={cn("flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium", STATUT_COLORS.ferme)}>
                        {STATUT_LABELS.ferme}
                      </span>
                    )}

                    <Button size="sm" variant="outline" className="text-xs h-7 px-2.5" onClick={() => setReassignModalOpen(true)}>
                      <UserCheck className="w-3 h-3 mr-1" /> Réassigner
                    </Button>

                    {detail.statut !== "ferme" ? (
                      <Button size="sm" variant="destructive" className="text-xs h-7 px-2.5" onClick={() => setCloseModalOpen(true)}>
                        Fermer
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="text-xs h-7 px-2.5" onClick={handleReopen}>
                        <RefreshCw className="w-3 h-3 mr-1" /> Rouvrir
                      </Button>
                    )}

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigator.clipboard.writeText(window.location.href)}>
                          <Copy className="w-3.5 h-3.5 mr-2" /> Copier le lien
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.print()}>
                          Imprimer la conversation
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <Button
                      size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground"
                      onClick={() => {
                        if (window.innerWidth < 1024) setMobileInfoOpen(o => !o);
                        else setInfoSidebarOpen(o => !o);
                      }}
                      title="Panneau d'informations"
                    >
                      <Info className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Content area */}
              <div className="flex-1 flex overflow-hidden">
                {/* Conversation + compose */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" onCopy={handlePlainTextCopy}>
                    {messagesLoading ? (
                      <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                    ) : messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <MessageCircle className="w-8 h-8 text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">Aucun message pour le moment.</p>
                      </div>
                    ) : (
                      messages.map(msg => (
                        <MessageBubble
                          key={msg.id}
                          msg={msg}
                          currentUserId={profile?.id || ""}
                          onImageClick={(images, idx) => { setLightboxImages(images); setLightboxIdx(idx); }}
                        />
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Compose */}
                  {detail.statut !== "ferme" && (
                    <div className={cn(
                      "border-t border-border px-4 py-3 bg-card shrink-0",
                    )}>
                      {/* Toggle réponse / note */}
                      <div className="flex items-center gap-1 mb-2">
                        <button
                          onClick={() => setComposeMode("reply")}
                          className={cn("px-3 py-1 rounded-md text-xs font-medium transition-colors",
                            composeMode === "reply" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary")}
                        >
                          Réponse
                        </button>
                        <button
                          onClick={() => setComposeMode("note")}
                          className={cn("px-3 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-colors",
                            composeMode === "note" ? "bg-amber-500 text-white" : "text-muted-foreground hover:bg-secondary")}
                        >
                          <Lock className="w-3 h-3" /> Note interne
                        </button>
                      </div>

                      {/* File previews */}
                      {composeFiles.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {composeFiles.map((f, i) => (
                            <div key={i} className="flex items-center gap-1.5 bg-secondary rounded-lg px-2 py-1 text-xs">
                              {f.type.startsWith("image/") ? <ImageIcon className="w-3 h-3" /> : f.type.startsWith("video/") ? <Video className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                              <span className="max-w-[100px] truncate">{f.name}</span>
                              <button onClick={() => setComposeFiles(prev => prev.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div
                        className={cn(
                          "border rounded-xl transition-colors",
                          composeDragging ? "border-primary bg-primary/5" : composeMode === "note" ? "border-amber-400 bg-amber-50/30 dark:bg-amber-900/10" : "border-border"
                        )}
                        onDragOver={e => { e.preventDefault(); setComposeDragging(true); }}
                        onDragLeave={() => setComposeDragging(false)}
                        onDrop={handleComposeDrop}
                      >
                        <textarea
                          data-testid="textarea-compose"
                          value={composeText}
                          onChange={e => setComposeText(e.target.value)}
                          onKeyDown={handleComposeKeyDown}
                          placeholder={composeMode === "note" ? "Note interne (non visible par le revendeur)…" : "Tapez votre message… (Cmd+Entrée pour envoyer)"}
                          rows={3}
                          className="w-full px-3 pt-2.5 bg-transparent text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none"
                        />
                        <div className="flex items-center justify-between px-3 pb-2">
                          <div className="flex items-center gap-0.5">
                            <button
                              data-testid="button-attach-files"
                              onClick={() => composeFileRef.current?.click()}
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                              title="Joindre des fichiers"
                            >
                              <Paperclip className="w-4 h-4" />
                            </button>
                            <button
                              data-testid="button-camera"
                              onClick={() => composeCameraRef.current?.click()}
                              className="md:hidden p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                              title="Prendre une photo"
                            >
                              <Camera className="w-4 h-4" />
                            </button>
                          </div>
                          <button
                            data-testid="button-send-message"
                            onClick={handleSendMessage}
                            disabled={(!composeText.trim() && composeFiles.length === 0) || composeSending}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                          >
                            {composeSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            Envoyer
                          </button>
                        </div>
                      </div>
                      <input
                        ref={composeFileRef}
                        type="file"
                        multiple
                        accept={ALLOWED_MIME.join(",")}
                        className="hidden"
                        onChange={e => handleComposeFiles(Array.from(e.target.files || []))}
                      />
                      <input
                        ref={composeCameraRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={e => handleComposeFiles(Array.from(e.target.files || []))}
                      />
                      <p className="text-[10px] text-muted-foreground mt-1 text-right">Cmd+Entrée pour envoyer · Max 10 fichiers</p>
                    </div>
                  )}
                </div>

                {/* Mobile Info Panel — slide-in overlay on < lg */}
                {mobileInfoOpen && (
                  <div className="lg:hidden fixed inset-0 z-50 flex">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setMobileInfoOpen(false)} />
                    <aside className="relative ml-auto w-80 max-w-[90vw] h-full bg-card border-l border-border overflow-y-auto p-4 flex flex-col gap-4 shadow-xl">
                      <div className="flex items-center justify-between shrink-0">
                        <span className="font-semibold text-sm">Informations</span>
                        <button onClick={() => setMobileInfoOpen(false)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {detail && (
                        <InfoSidebar
                          detail={detail}
                          participants={participants}
                          recentSn={recentSn}
                          onChatSelect={(id) => { selectChat(id); setMobileInfoOpen(false); }}
                        />
                      )}
                    </aside>
                  </div>
                )}

              {/* Info Sidebar */}
                {infoSidebarOpen && (
                  <aside className="hidden lg:flex flex-col w-72 border-l border-border bg-card overflow-y-auto p-4 gap-4 shrink-0">
                    <InfoSidebar
                      detail={detail}
                      participants={participants}
                      recentSn={recentSn}
                      onChatSelect={selectChat}
                    />
                  </aside>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ChatCard ─────────────────────────────────────────────────────────────────

function ChatCard({
  chat, isSelected, currentUserId, tab, onSelect, onAssign, assignLoading,
}: {
  chat: ChatSummary; isSelected: boolean; currentUserId: string;
  tab: Tab; onSelect: (id: string) => void; onAssign: (id: string) => void; assignLoading: boolean;
}) {
  const Icon = TYPE_ICON[chat.typeDemande];
  const { images, videos, docs } = chat.attachments;

  return (
    <div
      data-testid={`card-chat-${chat.id}`}
      onClick={() => onSelect(chat.id)}
      className={cn(
        "relative flex flex-col gap-1.5 px-4 py-3 cursor-pointer border-b border-border/50 transition-colors hover:bg-secondary/40",
        isSelected && "bg-primary/5 border-l-2 border-l-primary",
        chat.isUnread && !isSelected && "bg-blue-50/40 dark:bg-blue-900/10"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {/* Blue dot — unread indicator */}
          {chat.isUnread && !isSelected && (
            <span className="w-2 h-2 rounded-full bg-primary shrink-0 flex-none" aria-label="Non lu" />
          )}
          <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0", TYPE_COLORS[chat.typeDemande])}>
            <Icon className="w-3 h-3 inline mr-0.5" />{TYPE_LABELS[chat.typeDemande]}
          </span>
          <span className={cn("font-mono text-xs text-muted-foreground truncate", chat.isUnread && !isSelected && "font-bold text-foreground")}>
            {chat.numero}
          </span>
        </div>
        <span className="text-[11px] text-muted-foreground shrink-0 mt-0.5">{relativeDate(chat.lastMessageAt || chat.updatedAt)}</span>
      </div>

      <p className={cn("text-sm text-foreground truncate leading-snug", chat.isUnread && !isSelected && "font-semibold")}>{chat.titre}</p>

      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
        {chat.reseller && <span className="truncate max-w-[120px]">{chat.reseller.nom}</span>}
        {chat.robotSn && <><span>·</span><span className="font-mono truncate max-w-[90px]">{chat.robotSn.slice(0, 10)}</span></>}
        {chat.clientFinalNom && <><span>·</span><span className="truncate max-w-[80px]">{chat.clientFinalNom}</span></>}
      </div>

      {chat.lastMessage && (
        <p className={cn(
          "text-[12px] text-muted-foreground line-clamp-1",
          chat.isUnread && !isSelected ? "font-semibold not-italic" : "italic"
        )}>
          {(chat.lastMessage.contenu || "").slice(0, 80)}
        </p>
      )}

      <div className="flex items-center justify-between gap-2 mt-0.5">
        <div className="flex items-center gap-1.5">
          {images > 0 && <span className="text-[10px] flex items-center gap-0.5 text-muted-foreground"><ImageIcon className="w-3 h-3" />{images}</span>}
          {videos > 0 && <span className="text-[10px] flex items-center gap-0.5 text-muted-foreground"><Video className="w-3 h-3" />{videos}</span>}
          {docs > 0 && <span className="text-[10px] flex items-center gap-0.5 text-muted-foreground"><FileText className="w-3 h-3" />{docs}</span>}
          <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium", STATUT_COLORS[chat.statut])}>
            {STATUT_LABELS[chat.statut]}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {chat.technicien ? (
            <Avatar className="h-5 w-5 shrink-0">
              <AvatarImage src={chat.technicien.avatarUrl || undefined} />
              <AvatarFallback className="text-[8px]">{initiales(chat.technicien.prenom, chat.technicien.nom)}</AvatarFallback>
            </Avatar>
          ) : null}

          {tab === "nouveau" && (
            <button
              data-testid={`button-assign-${chat.id}`}
              onClick={e => { e.stopPropagation(); onAssign(chat.id); }}
              disabled={assignLoading}
              className="px-2 py-0.5 bg-primary text-primary-foreground rounded text-[10px] font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {assignLoading ? "…" : "Je prends"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MessageBubble ────────────────────────────────────────────────────────────

function MessageBubble({
  msg, currentUserId, onImageClick,
}: {
  msg: ChatMessage; currentUserId: string;
  onImageClick: (images: ChatAttachment[], idx: number) => void;
}) {
  const isJobotto = msg.auteur_type === "jobotto";
  const isSysteme = msg.auteur_type === "systeme";
  const isRevendeur = msg.auteur_type === "revendeur";
  const isNote = msg.est_note_interne;

  if (isSysteme) {
    return (
      <div className="flex justify-center my-1">
        <span className="text-[11px] text-muted-foreground italic bg-secondary/60 px-3 py-1 rounded-full">
          {msg.contenu}
        </span>
      </div>
    );
  }

  const prenom = isJobotto
    ? (msg.profiles?.first_name || "Jobotto")
    : (msg.portail_users?.prenom || "Revendeur");
  const nom = isJobotto
    ? (msg.profiles?.last_name || "")
    : (msg.portail_users?.nom || "");
  const avatarUrl = isJobotto ? msg.profiles?.avatar_url : undefined;

  const images = (msg.chat_attachments || []).filter(a => a.type_media === "image");
  const videos = (msg.chat_attachments || []).filter(a => a.type_media === "video");
  const docs = (msg.chat_attachments || []).filter(a => a.type_media === "document");

  return (
    <div className={cn("flex gap-2.5", isJobotto ? "flex-row-reverse" : "flex-row")}>
      <Avatar className="h-7 w-7 shrink-0 mt-1">
        <AvatarImage src={avatarUrl || undefined} />
        <AvatarFallback className={cn("text-[10px] font-bold", isJobotto ? "bg-primary/10 text-primary" : "bg-secondary text-foreground")}>
          {initiales(prenom, nom)}
        </AvatarFallback>
      </Avatar>

      <div className={cn("flex flex-col gap-1 max-w-[70%]", isJobotto ? "items-end" : "items-start")}>
        {/* Metadata */}
        <div className={cn("flex items-center gap-1.5 text-[11px] text-muted-foreground", isJobotto ? "flex-row-reverse" : "")}>
          <span className="font-medium text-foreground">{prenom} {nom}</span>
          <span>·</span>
          <span>{isJobotto ? "Jobotto" : "Revendeur"}</span>
          <span>·</span>
          <span>{formatTime(msg.created_at)}</span>
          {isNote && (
            <span className="flex items-center gap-0.5 text-amber-600 dark:text-amber-400 font-medium">
              <Lock className="w-2.5 h-2.5" /> Note interne
            </span>
          )}
        </div>

        {/* Bubble */}
        {(msg.contenu || images.length > 0 || videos.length > 0 || docs.length > 0) && (
          <div className={cn(
            "rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed break-words",
            isJobotto
              ? "bg-card border border-border text-foreground rounded-tr-sm"
              : "bg-primary/10 text-foreground rounded-tl-sm",
            isNote && "bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800"
          )}>
            {msg.contenu && <p className="whitespace-pre-wrap">{msg.contenu}</p>}

            {/* Images grid */}
            {images.length > 0 && (
              <div className={cn("grid gap-1 mt-2", images.length === 1 ? "grid-cols-1" : "grid-cols-2")}>
                {images.map((img, i) => (
                  <img
                    key={img.id}
                    src={img.signedUrl || ""}
                    alt={img.filename}
                    className="rounded-lg max-h-48 w-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => onImageClick(images, i)}
                  />
                ))}
              </div>
            )}

            {/* Videos */}
            {videos.map(vid => (
              <div key={vid.id} className="mt-2">
                <video src={vid.signedUrl || ""} controls className="rounded-lg max-h-48 w-full" />
              </div>
            ))}

            {/* Documents */}
            {docs.map(doc => (
              <a
                key={doc.id}
                href={doc.signedUrl || ""}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 mt-2 bg-secondary/60 rounded-lg px-3 py-2 hover:bg-secondary transition-colors text-xs"
              >
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="flex-1 truncate font-medium">{doc.filename}</span>
                <span className="text-muted-foreground shrink-0">{formatBytes(doc.size_bytes)}</span>
                <Download className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── InfoSidebar ──────────────────────────────────────────────────────────────

function InfoSidebar({
  detail, participants, recentSn, onChatSelect,
}: {
  detail: ChatDetail; participants: ChatParticipant[]; recentSn: any[]; onChatSelect: (id: string) => void;
}) {
  const openedBy = detail.ouvert_par_portail_user;
  const tech = detail.profiles;
  const reseller = detail.resellers;
  const robot = detail.pudu_robots;
  const hoursSinceOpen = (Date.now() - new Date(detail.createdAt).getTime()) / 3600000;
  const isOld = hoursSinceOpen > 24 && detail.statut !== "ferme";

  return (
    <>
      {/* Bloc Informations */}
      <InfoBlock title="Informations">
        <InfoRow label="Type" value={detail.typeDemande === "information" ? "Information" : detail.typeDemande === "installation" ? "Installation" : "SAV"} />
        <InfoRow label="Ouvert le" value={new Date(detail.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} />
        {openedBy && <InfoRow label="Par" value={`${openedBy.prenom || ""} ${openedBy.nom || ""}`.trim() || openedBy.email || "—"} />}
        {detail.assigneAt && tech && (
          <InfoRow label="Pris en charge" value={`${tech.first_name} ${tech.last_name} · ${new Date(detail.assigneAt).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`} />
        )}
        {isOld && (
          <div className="flex items-center gap-1.5 mt-1 text-amber-600 dark:text-amber-400 text-xs">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>{Math.floor(hoursSinceOpen)}h sans réponse Jobotto</span>
          </div>
        )}
      </InfoBlock>

      {/* Bloc Revendeur */}
      {reseller && (
        <InfoBlock title="Revendeur">
          <div className="flex items-center gap-2">
            {reseller.logo_url ? (
              <img src={reseller.logo_url} alt={reseller.company_name} className="h-8 w-8 rounded object-contain shrink-0" />
            ) : (
              <div className="h-8 w-8 rounded bg-secondary flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{reseller.company_name}</p>
              {reseller.code_court && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{reseller.code_court}</Badge>}
            </div>
          </div>
          {reseller.phone && <InfoRow label="Tél." value={reseller.phone} />}
          {reseller.email && <InfoRow label="Email" value={reseller.email} />}
        </InfoBlock>
      )}

      {/* Bloc Robot (SAV) */}
      {detail.typeDemande === "sav" && detail.robotSn && (
        <InfoBlock title="Robot">
          {robot && (
            <div className="flex items-center gap-2 mb-2">
              <Bot className="w-5 h-5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm font-medium">{robot.robot_type || "Robot"}</p>
                {robot.robot_name && <p className="text-xs text-muted-foreground">{robot.robot_name}</p>}
              </div>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-xs bg-secondary px-2 py-0.5 rounded">{detail.robotSn}</span>
            <button onClick={() => navigator.clipboard.writeText(detail.robotSn || "")} className="text-muted-foreground hover:text-foreground">
              <Copy className="w-3 h-3" />
            </button>
          </div>
          {robot?.status && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className={cn("w-2 h-2 rounded-full", robot.status === "online" ? "bg-green-500" : "bg-gray-400")} />
              <span className="text-xs text-muted-foreground capitalize">{robot.status}</span>
              {robot.last_seen && <span className="text-xs text-muted-foreground">· {relativeDate(robot.last_seen)}</span>}
            </div>
          )}
          {recentSn.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Chats précédents pour ce SN</p>
              {recentSn.map((c: any) => (
                <button key={c.id} onClick={() => onChatSelect(c.id)}
                  className="w-full text-left flex items-center gap-1.5 text-xs hover:text-primary transition-colors">
                  <span className="font-mono">{c.numero}</span>
                  <span className={cn("px-1.5 py-0.5 rounded-full text-[10px]", STATUT_COLORS[c.statut as Statut] || "")}>{STATUT_LABELS[c.statut as Statut] || c.statut}</span>
                </button>
              ))}
            </div>
          )}
        </InfoBlock>
      )}

      {/* Bloc Client final */}
      {(detail.typeDemande === "sav" || detail.typeDemande === "installation") && detail.clientFinalNom && (
        <InfoBlock title="Client final">
          <p className="text-sm font-medium">{detail.clientFinalNom}</p>
          {detail.clientFinalAdresse && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{detail.clientFinalAdresse}</p>}
        </InfoBlock>
      )}

      {/* Bloc Participants */}
      <InfoBlock title="Participants">
        {participants.length === 0 ? (
          <p className="text-xs text-muted-foreground">Aucun participant.</p>
        ) : (
          <div className="space-y-2">
            {participants.map(p => {
              const isJobotto = p.type_acteur === "jobotto";
              const prenom = isJobotto ? p.profiles?.first_name : p.portail_users?.prenom;
              const nom = isJobotto ? p.profiles?.last_name : p.portail_users?.nom;
              const email = isJobotto ? p.profiles?.email : p.portail_users?.email;
              return (
                <div key={p.id} className="flex items-center gap-2">
                  <Avatar className="h-6 w-6 shrink-0">
                    <AvatarImage src={isJobotto ? p.profiles?.avatar_url : undefined} />
                    <AvatarFallback className="text-[9px]">{initiales(prenom, nom)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{prenom} {nom}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{isJobotto ? "Jobotto" : "Revendeur"}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </InfoBlock>
    </>
  );
}

function InfoBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-border rounded-xl p-3 space-y-2">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">{title}</p>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-xs text-muted-foreground shrink-0 w-20">{label}</span>
      <span className="text-xs text-foreground break-words min-w-0">{value}</span>
    </div>
  );
}

